import React from 'react'
import { useLocation } from 'react-router-dom'
import { SessionContext, useGetReconciliationContextQuery } from '../generated/graphql'
import { ReconciliationStep } from '../models/reconciliation'
import { ReconciliationRecordType } from '../pages/Reconciliation/Layout/RecordSelector'
import { getNextStepPath, getStepFromPath, hasWorkflowBeenCompleted, mapToStep } from '../utils/stepMap'
import useReconciliationContext, { Context } from './useReconciliationContext'

interface UseReconciliationForcedNavigation {
  isContextLoading: boolean
  contextHasErrors: boolean
  contextError: unknown
  refetchContext: () => void
  redirectTo: string | null
}

export default function useReconciliationForcedNavigation(accountId: number): UseReconciliationForcedNavigation {
  const { pathname } = useLocation()
  const step = getStepFromPath(pathname) ?? ReconciliationStep.AccountInformation

  const {
    reconciliationContext,
    storeContext,
    setRecordSlotA,
    setRecordSlotB,
    setDisableRecordSelector,
    setMainWorkflowCompleted,
  } = useReconciliationContext()

  const {
    data,
    error: contextError,
    refetch: refetchContext,
    isError: contextHasErrors,
    isFetching,
  } = useGetReconciliationContextQuery(
    { accountId },
    {
      queryKey: ['GetReconciliationContext', { accountId }, pathname],
      refetchOnWindowFocus: false,
      retry: false,
      refetchOnMount: true,
      gcTime: 0,
    },
  )
  const fetchedContext = data?.reconciliationContext

  React.useEffect(() => {
    if (!fetchedContext) {
      return
    }

    storeContext?.(fetchedContext)
    setDisableRecordSelector?.(false)
    setRecordSlotA?.(ReconciliationRecordType.TaxPayerVersion)
    setRecordSlotB?.(ReconciliationRecordType.AuditorVersion)
    setMainWorkflowCompleted?.(hasWorkflowBeenCompleted(fetchedContext.completedSteps.map(mapToStep)))
  }, [fetchedContext, storeContext, setDisableRecordSelector, setRecordSlotA, setRecordSlotB, setMainWorkflowCompleted])

  const isContextLoading = isFetching || !fetchedContext || !isSameContext(reconciliationContext, fetchedContext)
  const completedSteps = fetchedContext?.completedSteps.map(mapToStep) ?? []
  const redirectTo =
    !isContextLoading && !hasPermissions(step, completedSteps) ? getNextStepPath(accountId, completedSteps) : null

  return { isContextLoading, contextHasErrors, contextError, refetchContext, redirectTo }
}

function isSameContext(stored: Context | null, fetched: SessionContext): boolean {
  return (
    stored !== null &&
    stored.accountId === fetched.accountId &&
    stored.taxYear === fetched.taxYear &&
    stored.completedSteps.join() === fetched.completedSteps.map(mapToStep).join()
  )
}

function hasPermissions(step: ReconciliationStep, completedSteps: ReconciliationStep[]): boolean {
  const permissionsByStep = {
    [ReconciliationStep.Contacts]: [ReconciliationStep.AccountInformation],
    [ReconciliationStep.PropertyAssetList]: [ReconciliationStep.AccountInformation, ReconciliationStep.Contacts],
    [ReconciliationStep.LeasedAssetList]: [
      ReconciliationStep.AccountInformation,
      ReconciliationStep.Contacts,
      ReconciliationStep.PropertyAssetList,
    ],
    [ReconciliationStep.Summary]: [
      ReconciliationStep.AccountInformation,
      ReconciliationStep.Contacts,
      ReconciliationStep.PropertyAssetList,
      ReconciliationStep.LeasedAssetList,
    ],
    [ReconciliationStep.Reconciled]: [
      ReconciliationStep.AccountInformation,
      ReconciliationStep.Contacts,
      ReconciliationStep.PropertyAssetList,
      ReconciliationStep.LeasedAssetList,
      ReconciliationStep.Summary,
    ],
  }

  if (step === ReconciliationStep.AccountInformation) {
    return true
  }

  const permissions = permissionsByStep[step]
  return permissions.every((permission) => completedSteps.includes(permission))
}
